import React, { ComponentType, ReactNode } from "react";

import { AgentServiceProvider } from "../../contexts/agent-service-context";
import { CallServiceProvider } from "../../contexts/call-service-context";
import { ChapterMediaLinkServiceProvider } from "../../contexts/chapter-media-link-service-context";
import { ChapterProjectServiceProvider } from "../../contexts/chapter-project-service-context";
import { ChapterServiceProvider } from "../../contexts/chapter-service-context";
import { ConciergeServiceProvider } from "../../contexts/concierge-service-context";
import { CustomerRelationServiceProvider } from "../../contexts/customer-relationship-service-context";
import { CustomerServiceProvider } from "../../contexts/customer-service-context";
import { DashboardServiceProvider } from "../../contexts/dashboard-service-context";
import { DevelopmentMediaLinkServiceProvider } from "../../contexts/development-media-link-service-context";
import { DevelopmentOpportunityGuestServiceProvider } from "../../contexts/development-opportunity-guest-service-context";
import { DevelopmentOpportunityServiceProvider } from "../../contexts/development-opportunity-service-context";
import { DevelopmentPriceNoteServiceProvider } from "../../contexts/development-price-note-service-context";
import { DevelopmentProjectServiceProvider } from "../../contexts/development-project-service-context";
import { DropdownOptionsServiceProvider } from "../../contexts/dropdown-options-service-context";
import { EnquiryServiceProvider } from "../../contexts/enquiry-service-context";
import { HOMApprovalServiceProvider } from "../../contexts/hom-approval-service-context";
import { HOMPropertyServiceProvider } from "../../contexts/hom-property-service-context";
import { HomeAcquisitionServiceProvider } from "../../contexts/home-acquisition-service-context";
import { LoginServiceProvider } from "../../contexts/login-service-context";
import { MasterServiceProvider } from "../../contexts/masters-service-context";
import { OutboundOpportunityServiceProvider } from "../../contexts/outbound-opportunity-service-context";
import { PostSalesInvoiceServiceProvider } from "../../contexts/post-sales-invoice-service-context";
import { RentalCalendarServiceProvider } from "../../contexts/rental-calendar-service-context";
import { RentalCommissionApprovalServiceProvider } from "../../contexts/rental-commission-approval-service-context";
import { RentalCostCentreApprovalServiceProvider } from "../../contexts/rental-cost-centre-approval-service-context";
import { RentalDepositApprovalServiceProvider } from "../../contexts/rental-deposit-approval-service-context";
import { RentalForceBookApprovalServiceProvider } from "../../contexts/rental-force-book-approval-service-context";
import { RentalInvoiceApprovalServiceProvider } from "../../contexts/rental-invoice-approval-service-context";
import { RentalMastersServiceProvider } from "../../contexts/rental-masters-service";
import { RentalPaymentApprovalServiceProvider } from "../../contexts/rental-payment-approval-service-context";
import { RentalPropertyServiceProvider } from "../../contexts/rental-property-service-context";
import { RentalTripApprovalServiceProvider } from "../../contexts/rental-trip-approval-service-context";
import { RentalTripGuestServiceProvider } from "../../contexts/rental-trip-guest-service-context";
import { RentalTripPaymentServiceProvider } from "../../contexts/rental-trip-payment-service-context";
import { RentalTripServiceProvider } from "../../contexts/rental-trip-service-context";
import { SoleneServiceProvider } from "../../contexts/solene-service-context";
import { StaffServiceProvider } from "../../contexts/staff-service-context";
import { TaskServiceProvider } from "../../contexts/task-service-context";
import { IAgentService } from "../../types/services/agents-service";
import { ICallService } from "../../types/services/call-service";
import { IChapterMediaLinkService } from "../../types/services/chapter-media-link-service";
import { IChapterProjectService } from "../../types/services/chapter-project-service";
import { IChapterService } from "../../types/services/chapter-service";
import { IConciergeService } from "../../types/services/concierge-service";
import { ICustomerRelationService } from "../../types/services/customer-relation-service";
import { ICustomerService } from "../../types/services/customer-service";
import { IDashboardService } from "../../types/services/dashboard-service";
import { IDevelopmentMediaLinkService } from "../../types/services/development-media-link-service";
import { IDevelopmentOpportunityGuestService } from "../../types/services/development-opportunity-guest-service";
import { IDevelopmentOpportunityService } from "../../types/services/development-opportunity-service";
import { IDevelopmentPriceNoteService } from "../../types/services/development-price-note-service";
import { IDevelopmentProjectService } from "../../types/services/development-project-service";
import { IDropdownOptionsService } from "../../types/services/dropdown-options-service";
import { IEnquiryService } from "../../types/services/enquiry-service";
import { IHOMApprovalService } from "../../types/services/hom-approval-service";
import { IHOMPropertyService } from "../../types/services/hom-property-service";
import { IHomeAcquisitionService } from "../../types/services/home-acquisition-service";
import { ILoginService } from "../../types/services/login-service";
import { IMastersService } from "../../types/services/masters-service";
import { IOutboundOpportunityService } from "../../types/services/outbound-opportunity-service";
import { IPostSalesInvoiceService } from "../../types/services/post-sales-invoice-service";
import { IRentalCalendarService } from "../../types/services/rental-calendar-service";
import { IRentalCommissionApprovalService } from "../../types/services/rental-commission-approval-service";
import { IRentalCostCentreApprovalService } from "../../types/services/rental-cost-centre-approval-service";
import { IRentalDepositApprovalService } from "../../types/services/rental-deposit-approval-service";
import { IRentalForceBookApprovalService } from "../../types/services/rental-force-book-approval-service";
import { IRentalInvoiceApprovalService } from "../../types/services/rental-invoice-approval-service";
import { IRentalMastersService } from "../../types/services/rental-masters-service";
import { IRentalPaymentApprovalService } from "../../types/services/rental-payment-approval-service";
import { IRentalPropertyService } from "../../types/services/rental-property-service";
import { IRentalTripApprovalService } from "../../types/services/rental-trip-approval-service";
import { IRentalTripGuestService } from "../../types/services/rental-trip-guest-service";
import { IRentalTripPaymentService } from "../../types/services/rental-trip-payment-service";
import { IRentalTripService } from "../../types/services/rental-trip-service";
import { ISoleneService } from "../../types/services/solene-sevice";
import { IStaffService } from "../../types/services/staff-service";
import { ITaskService } from "../../types/services/task-service";

type Provider<T = any> = [
  ComponentType<{ service: T; children: ReactNode }>,
  T,
];

export interface ServiceProviderProps {
  agentService: IAgentService;
  callService: ICallService;
  customerService: ICustomerService;
  customerRelationService: ICustomerRelationService;
  dashboardService: IDashboardService;
  developmentOpportunityService: IDevelopmentOpportunityService;
  developmentOpportunityGuestService: IDevelopmentOpportunityGuestService;
  developmentProjectService: IDevelopmentProjectService;
  developmentMediaLinkService: IDevelopmentMediaLinkService;
  developmentPriceNoteService: IDevelopmentPriceNoteService;
  chapterService: IChapterService;
  chapterProjectService: IChapterProjectService;
  chapterMediaLinkService: IChapterMediaLinkService;
  dropdownOptionsService: IDropdownOptionsService;
  enquiryService: IEnquiryService;
  loginService: ILoginService;
  masterService: IMastersService;
  rentalCalendarService: IRentalCalendarService;
  rentalMastersService: IRentalMastersService;
  rentalPropertyService: IRentalPropertyService;
  rentalTripService: IRentalTripService;
  soleneService: ISoleneService;
  rentalTripGuestService: IRentalTripGuestService;
  rentalTripPaymentService: IRentalTripPaymentService;
  staffService: IStaffService;
  taskService: ITaskService;
  homeAcquisitionService: IHomeAcquisitionService;
  conciergeService: IConciergeService;
  outboundOpportunityService: IOutboundOpportunityService;
  rentalTripApprovalService: IRentalTripApprovalService;
  rentalCommissionApprovalService: IRentalCommissionApprovalService;
  rentalPaymentApprovalService: IRentalPaymentApprovalService;
  rentalDepositApprovalService: IRentalDepositApprovalService;
  rentalInvoiceService: IRentalInvoiceApprovalService;
  rentalForceBookApprovalService: IRentalForceBookApprovalService;
  rentalCostCentreApprovalService: IRentalCostCentreApprovalService;
  homApprovalService: IHOMApprovalService;
  homPropertyService: IHOMPropertyService;
  postSalesInvoiceService: IPostSalesInvoiceService;
  children: ReactNode;
}

function ServiceProvider(props: ServiceProviderProps) {
  const {
    agentService,
    callService,
    customerService,
    customerRelationService,
    dashboardService,
    developmentOpportunityGuestService,
    developmentOpportunityService,
    developmentProjectService,
    developmentMediaLinkService,
    developmentPriceNoteService,
    chapterService,
    chapterProjectService,
    chapterMediaLinkService,
    dropdownOptionsService,
    enquiryService,
    loginService,
    masterService,
    rentalCalendarService,
    rentalMastersService,
    rentalPropertyService,
    rentalTripService,
    soleneService,
    rentalTripGuestService,
    rentalTripPaymentService,
    staffService,
    taskService,
    homeAcquisitionService,
    conciergeService,
    outboundOpportunityService,
    rentalTripApprovalService,
    rentalPaymentApprovalService,
    rentalDepositApprovalService,
    rentalInvoiceService,
    rentalForceBookApprovalService,
    rentalCommissionApprovalService,
    rentalCostCentreApprovalService,
    homApprovalService,
    homPropertyService,
    postSalesInvoiceService,
    children,
  } = props,
    providerTemplate: Provider[] = [
      [AgentServiceProvider, agentService],
      [CallServiceProvider, callService],
      [CustomerServiceProvider, customerService],
      [CustomerRelationServiceProvider, customerRelationService],
      [DashboardServiceProvider, dashboardService],
      [DevelopmentOpportunityServiceProvider, developmentOpportunityService],
      [DevelopmentOpportunityGuestServiceProvider, developmentOpportunityGuestService],
      [DevelopmentProjectServiceProvider, developmentProjectService],
      [DevelopmentMediaLinkServiceProvider, developmentMediaLinkService],
      [DevelopmentPriceNoteServiceProvider, developmentPriceNoteService],
      [ChapterServiceProvider, chapterService],
      [ChapterProjectServiceProvider, chapterProjectService],
      [ChapterMediaLinkServiceProvider, chapterMediaLinkService],
      [DropdownOptionsServiceProvider, dropdownOptionsService],
      [EnquiryServiceProvider, enquiryService],
      [LoginServiceProvider, loginService],
      [MasterServiceProvider, masterService],
      [RentalCalendarServiceProvider, rentalCalendarService],
      [RentalMastersServiceProvider, rentalMastersService],
      [RentalPropertyServiceProvider, rentalPropertyService],
      [RentalTripServiceProvider, rentalTripService],
      [SoleneServiceProvider, soleneService],
      [RentalTripPaymentServiceProvider, rentalTripPaymentService],
      [StaffServiceProvider, staffService],
      [TaskServiceProvider, taskService],
      [RentalTripGuestServiceProvider, rentalTripGuestService],
      [HomeAcquisitionServiceProvider, homeAcquisitionService],
      [ConciergeServiceProvider, conciergeService],
      [OutboundOpportunityServiceProvider, outboundOpportunityService],
      [RentalTripApprovalServiceProvider, rentalTripApprovalService],
      [RentalPaymentApprovalServiceProvider, rentalPaymentApprovalService],
      [RentalDepositApprovalServiceProvider, rentalDepositApprovalService],
      [RentalInvoiceApprovalServiceProvider, rentalInvoiceService],
      [RentalForceBookApprovalServiceProvider, rentalForceBookApprovalService],
      [RentalCommissionApprovalServiceProvider, rentalCommissionApprovalService],
      [RentalCostCentreApprovalServiceProvider, rentalCostCentreApprovalService],
      [HOMApprovalServiceProvider, homApprovalService],
      [HOMPropertyServiceProvider, homPropertyService],
      [PostSalesInvoiceServiceProvider, postSalesInvoiceService],
    ];

  return (
    <>
      {providerTemplate.reduce((children, [Provider, service]) => {
        return <Provider service={service}>{children}</Provider>;
      }, children as ReactNode)}
    </>
  );
}

export default ServiceProvider;
